import { AlbumContentType } from 'common/enums';
import { uploadBlobToS3 } from 'helpers/upload-blob-to-s3';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { PendingOp } from './types';

export type EntryKind = 'video' | 'audio' | 'image' | 'document' | 'text';

export const getEntryKind = (content: AlbumContentSerialized): EntryKind => {
  switch (content.contentType) {
    case AlbumContentType.video:
      return 'video';
    case AlbumContentType.audio:
      return 'audio';
    case AlbumContentType.image:
      return 'image';
    case AlbumContentType.write:
      return 'text';
    default:
      return 'document';
  }
};

const getSourceUrl = (content: AlbumContentSerialized): string | undefined =>
  // mediaUrl is the canonical media URL on AlbumContentSerialized.
  // For video clips the playable file lives on content.content.videoUrl.
  content.mediaUrl ||
  content.content?.videoUrl ||
  content.thumbnailUrl ||
  undefined;

const extractSignedId = (url: string): string | null => {
  // Matches /rails/active_storage/blobs/redirect/<signed_id>/<filename>
  // or /rails/active_storage/representations/redirect/<signed_id>/<variation_key>/<filename>
  const match = url.match(
    /\/rails\/active_storage\/(?:blobs|representations)\/(?:redirect|proxy)\/([^/]+)/,
  );
  return match ? match[1] : null;
};

const guessFileName = (
  url: string,
  fallback: string,
  defaultExt: string,
): string => {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const last = path.split('/').filter(Boolean).pop();
    if (last && /\.[a-z0-9]+$/i.test(last)) return last;
  } catch {
    /* ignore */
  }
  return `${fallback}${defaultExt}`;
};

const downloadAsFile = async (url: string, fileName: string): Promise<File> => {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`Failed to download source media (${res.status})`);
  }
  const blob = await res.blob();
  return new File([blob], fileName, {
    type: blob.type || 'application/octet-stream',
  });
};

export type ApplyResult = {
  ok: number;
  failed: number;
  failedOps: PendingOp[];
};

export type ApplyOptions = {
  vaultId: number;
  contentsById: Map<number, AlbumContentSerialized>;
  ops: PendingOp[];
  onProgress?: (done: number, total: number) => void;
  onOpStart?: (op: PendingOp) => void;
  onOpDone?: (op: PendingOp, ok: boolean) => void;
};

const applyOne = async (
  vaultId: number,
  content: AlbumContentSerialized,
  op: PendingOp,
): Promise<void> => {
  const kind = getEntryKind(content);

  if (kind === 'text') {
    // Journal entry: re-create on the target album using existing body.
    const body =
      // The journal body lives on the underlying content payload.
      // It may come back as `body` on the content; fall back to description.
      (content.content as any)?.body || content.description || '';

    await VaultsController.addJournalEntry(op.targetAlbumId, {
      name: content.name,
      body,
    });
  } else {
    const sourceUrl = getSourceUrl(content);
    if (!sourceUrl) throw new Error('Entry has no downloadable media URL');

    let defaultExt = '.bin';
    if (kind === 'video') defaultExt = '.mp4';
    else if (kind === 'audio') defaultExt = '.m4a';
    else if (kind === 'image') defaultExt = '.jpg';
    else if (kind === 'document') defaultExt = '.pdf';

    let signedId = extractSignedId(sourceUrl);

    if (!signedId) {
      const fileName = guessFileName(
        sourceUrl,
        content.name || `entry-${content.id}`,
        defaultExt,
      );
      const file = await downloadAsFile(sourceUrl, fileName);
      const uploaded = await uploadBlobToS3(file);
      signedId = uploaded.signedId;
    }

    if (kind === 'video' || kind === 'audio') {
      await VaultsController.createClip(vaultId, {
        clip: {
          videoSignedId: signedId,
          name: content.name,
          recordedAt: new Date(),
          isAudioOnly: kind === 'audio',
          isPrivate: content.isPrivate,
          albumId: op.targetAlbumId,
        },
      });
    } else {
      await VaultsController.createDocument(vaultId, {
        clip: {
          videoSignedId: signedId,
          name: content.name,
          recordedAt: new Date(),
          isPrivate: content.isPrivate,
          albumId: op.targetAlbumId,
        },
      });
    }
  }

  if (op.action === 'move') {
    // Move = copy + delete; only delete after the copy succeeded.
    await VaultsController.deleteAlbumContent(content.id);
  }
};

export const applyPendingOps = async ({
  vaultId,
  contentsById,
  ops,
  onProgress,
  onOpStart,
  onOpDone,
}: ApplyOptions): Promise<ApplyResult> => {
  let ok = 0;
  let failed = 0;
  const failedOps: PendingOp[] = [];

  // Sequential — keeps server load predictable and avoids race conditions
  // on move-then-delete.
  for (let i = 0; i < ops.length; i += 1) {
    const op = ops[i];
    onOpStart?.(op);
    const content = contentsById.get(op.contentId);
    try {
      if (!content) throw new Error('Source entry no longer exists');
      // eslint-disable-next-line no-await-in-loop
      await applyOne(vaultId, content, op);
      ok += 1;
      onOpDone?.(op, true);
    } catch (err) {
      failed += 1;
      failedOps.push(op);
      onOpDone?.(op, false);
    }
    onProgress?.(i + 1, ops.length);
  }

  return { ok, failed, failedOps };
};
